import { AppForm }                from 'components/App/AppForm';
import { withNavigation }         from 'components/App/withNavigation';
import { computed }               from 'mobx';
import React                      from 'react';
import { Button }                 from 'antd';
import { Table }                  from 'antd';
import { Popconfirm }             from 'antd';
import { observer }               from 'mobx-react';
import { CloseCircleOutlined }    from '@ant-design/icons';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { CheckCircleOutlined }    from '@ant-design/icons';
import { PagedCollection }        from 'collections/PagedCollection';
import { getRoleIntlLabel }       from 'constants/RoleReferences';
import { appStore }               from 'stores/AppStore';
import { messageErrorIntl }       from 'utils/messageIntl';
import { messageSuccessIntl }     from 'utils/messageIntl';
import AdministratorForm          from 'forms/AdministratorForm';
import currentSession             from 'utils/currentSession';
import { rolesBack }              from 'constants/RoleReferences';
import FormattedMessageWrapper    from 'components/App/FormattedMessageWrapper';
import Search                     from 'components/App/IntlSearch';
import PageTitle                  from 'components/App/PageTitle';
import type { ColumnsType }       from 'antd/es/table';
import UserModel                  from 'models/UserModel';
import { WithNavigationProps }    from 'components/App/withNavigation';

interface AdministratorsState {
  table: {
    filters: Record<string, any>,
    itemsPerPage: number;
    page: number;
    sorts: Record<string, 'asc'|'desc'>;
  },
}

@observer
class Administrators extends React.Component<WithNavigationProps> {

  public state: AdministratorsState = {
    table: {
      filters: {
        'role.reference': rolesBack,
      },
      itemsPerPage: 10,
      page: 1,
      sorts: {
        enabled: 'asc',
      },
    },
  };

  public userCollection = new PagedCollection(UserModel);

  public componentDidMount() {
    if (!currentSession.isAdmin) {
      this.props.navigate('/404');
      return;
    }

    this.fetchData();
  }

  public render() {
    return (
      <>
        <PageTitle
          title={<FormattedMessageWrapper id="Administrateurs" />}
          topRightWidget={this.renderTopRightWidget()}
        />

        <div className="accented-shadow" style={contentStyle}>
          <Table
            columns={this.columns}
            dataSource={[...this.userCollection.models]}
            loading={this.userCollection.isLoading}
            onChange={this.onChange}
            onRow={record => ({
              onClick: () => AppForm.open(AdministratorForm, {
                model: record,
                onSuccess: async () => {
                  await this.fetchData();
                },
              }, {
                title: <FormattedMessageWrapper id="Modifier un administrateur" />,
              }),
            })}
            pagination={{
              current: this.userCollection.page,
              defaultCurrent: this.userCollection.page,
              hideOnSinglePage: true,
              pageSize: this.userCollection.itemsPerPage,
              placement: ['topEnd', 'bottomEnd'],
              size: 'medium',
              total: this.userCollection.total,
            }}
            rowKey={model => model.get('id')}
            size="small"
          />
        </div>
      </>
    );
  }

  public renderTopRightWidget = () => (
    <>
      <Search
        onSearch={this.onSearch}
        placeholder="Rechercher un administrateur"
        style={{ width: 200 }}
      />
      &nbsp;
      <Button
        htmlType="button"
        onClick={() => AppForm.open(AdministratorForm, {
          model: new UserModel(),
          onSuccess: async () => {
            await this.fetchData();
          },
        }, {
          title: <FormattedMessageWrapper id="Ajouter un administrateur" />,
        })}
      >
        <FormattedMessageWrapper id="Ajouter un administrateur" />
      </Button>
    </>
  );

  private deleteUser = async (id: id) => {
    const user = new UserModel();
    user.set('id', id);

    try {
      await user.destroy();

      messageSuccessIntl("Utilisateur supprimé !");

      this.fetchData();
    } catch {
      messageErrorIntl("Une erreur s'est produite.");
    }
  };

  private fetchData = async () => {
    this.userCollection.itemsPerPage = this.state.table.itemsPerPage;
    this.userCollection.filters = this.state.table.filters;
    this.userCollection.setPage(this.state.table.page);
    this.userCollection.sorts = this.state.table.sorts;

    await this.userCollection.list();
  };

  private getColumnSortOrder = (name: string) => {
    const { sorts } = this.userCollection;

    if (sorts[name]) {
      if (sorts[name] === 'asc') {
        return 'ascend';
      }

      if (sorts[name] === 'desc') {
        return 'descend';
      }
    }

    return null;
  };

  private onChange = (pagination: any, _filters: any, sorter: any) => {
    let sorts;
    if (!sorter.field || !sorter.order) {
      sorts = {};
    } else {
      const order = sorter.order === 'ascend' ? 'asc' : 'desc';
      sorts = {
        [sorter.field]: order,
      };
    }

    this.setState(
      {
        table: {
          filters: {
            'role.reference': rolesBack,
          },
          itemsPerPage: pagination.pageSize,
          page: pagination.current,
          sorts,
        },
      },
      () => this.fetchData(),
    );
  };

  private onSearch = (value?: string) => {
    this.setState((prevState: AdministratorsState)=> {
      const newStateTable = { ...prevState.table };
      delete newStateTable.filters.search;

      if (value !== '') {
        newStateTable.filters.search = value;
      }

      return { table: newStateTable };
    }, this.fetchData);
  };

  @computed
  get columns(): ColumnsType<UserModel> {
    const { roleCollection } = appStore;

    return [
      {
        dataIndex: 'role',
        render: (_, user) => {
          const role = roleCollection.findBy('id', user.roleId);
          if (!role) {
            return null;
          }
          return <b>{getRoleIntlLabel(role)}</b>;
        },
        sorter: true,
        sortOrder: this.getColumnSortOrder('role'),
        title: <FormattedMessageWrapper id="Rôle" />,
      },
      {
        dataIndex: 'company',
        render: (_, user) => <b>{user.company}</b>,
        sorter: true,
        sortOrder: this.getColumnSortOrder('company'),
        title: <FormattedMessageWrapper id="Société" />,
      },
      {
        dataIndex: 'firstName',
        sorter: true,
        sortOrder: this.getColumnSortOrder('firstName'),
        title: <FormattedMessageWrapper id="Prénom" />,
      },
      {
        dataIndex: 'lastName',
        sorter: true,
        sortOrder: this.getColumnSortOrder('lastName'),
        title: <FormattedMessageWrapper id="Nom" />,
      },
      {
        dataIndex: 'phone',
        sorter: true,
        sortOrder: this.getColumnSortOrder('phone'),
        title: <FormattedMessageWrapper id="Téléphone" />,
      },
      {
        dataIndex: 'email',
        sorter: true,
        sortOrder: this.getColumnSortOrder('email'),
        title: <FormattedMessageWrapper id="Email" />,
      },
      {
        dataIndex: 'enabled',

        render: enabled =>
          enabled ? (
            <CheckCircleOutlined style={{ color: '#2ecc71' }} />
          ) : (
            <CloseCircleOutlined style={{ color: '#e74c3c' }} />
          ),
        sorter: true,
        sortOrder: this.getColumnSortOrder('enabled'),
        title: <FormattedMessageWrapper id="Actif" />,
      },
      {
        dataIndex: 'actions',
        render: (_: string, record) => (
          <Popconfirm
            cancelText={<FormattedMessageWrapper id="Non" />}
            icon={<QuestionCircleOutlined style={{ color: '#e74c3c' }} />}
            okText={<FormattedMessageWrapper id="Oui" />}
            onCancel={event => event?.stopPropagation()}
            onConfirm={async event => {
              event?.stopPropagation();
              await this.deleteUser(record.get('id'));
              await this.fetchData();
            }}
            placement="left"
            title={
              <FormattedMessageWrapper id="Êtes-vous sûr de vouloir supprimer cet utilisateur ?" />
            }
          >
            <Button
              htmlType="button"
              onClick={event => {
                event.stopPropagation();
              }}
            >
              <FormattedMessageWrapper id="Supprimer" />
            </Button>
          </Popconfirm>
        ),
        title: <FormattedMessageWrapper id="Actions" />,
      },
    ];
  }
}

const contentStyle = { background: '#fff', minHeight: 360, padding: 24 };

const AdministratorsWithNavigation = withNavigation(Administrators);

export default AdministratorsWithNavigation;
